/* eslint-disable @typescript-eslint/ban-ts-comment */
import useQuery from 'hooks/useQuery'
import * as React from 'react'
import { useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import BaseField from 'ui/molecules/BaseField'
import Select from 'ui/Select'

import useSelectPagination from '@/components/SendResource/hooks/useSelectPagination'
import { useLoadCustomers } from '@/queries/customers/useLoadCustomer'
import { useChangePacketResponseCustomer } from '@/queries/packetResponses/useChangePacketResponseCustomer'
import { useChangeResponseCustomer } from '@/queries/responses/useChangeResponseCustomer'
import Modal from '@/ui/atoms/Modal'
import Space from '@/ui/atoms/Space'
import { buildFullName } from '@/utils/helpers'

type CustomerOption = {
  value: string
  label: string
}

function CustomerSelectField({
  fetchData,
  isModalOpen,
  setIsModalOpen,
  isAnonymous,
}: {
  fetchData: () => Promise<void>
  isModalOpen: boolean
  setIsModalOpen: (isModalOpen: boolean) => void
  isAnonymous: boolean
}) {
  const query = useQuery()
  const responseType = query.get('response_type')
  const { responseId } = useParams<{ responseId: string }>()
  const { changeResponseCustomer, isLoading: isLoadingChangeResponseCustomer } =
    useChangeResponseCustomer()
  const {
    changePacketResponseCustomer,
    isLoading: isLoadingChangePacketResponseCustomer,
  } = useChangePacketResponseCustomer()
  const [selectedCustomerId, setSelectedCustomerId] = React.useState<
    string | null
  >(null)

  const handleChangeResponseCustomer = React.useCallback(async () => {
    if (selectedCustomerId && responseId) {
      if (responseType !== 'packet') {
        await changeResponseCustomer(responseId, {
          customer_id: selectedCustomerId,
        })
      } else {
        await changePacketResponseCustomer(responseId, {
          customer_id: selectedCustomerId,
        })
      }
    }

    toast.success('Response transferred successfully')
    await fetchData()
    setIsModalOpen(false)
  }, [
    selectedCustomerId,
    responseId,
    fetchData,
    setIsModalOpen,
    responseType,
    changeResponseCustomer,
    changePacketResponseCustomer,
  ])
  const [hasMore, setHasMore] = React.useState(false)
  const { params, onInputChange, onMenuScrollToBottom, setOptions, options } =
    useSelectPagination(hasMore)
  const {
    data,
    isLoading: isLoadingLoadCustomers,
    isFetching,
  } = useLoadCustomers(
    { ...params },
    {
      keepPreviousData: true,
    }
  )

  React.useEffect(() => {
    setHasMore(Boolean(data?.next))
  }, [data?.next])

  React.useEffect(() => {
    const options = data?.results?.length
      ? data?.results.map((result) => ({
          value: result.id,
          label: buildFullName(result.first_name, result.last_name),
        }))
      : []

    setOptions(options)
  }, [data?.results, setOptions])

  const dropdownPlaceholder = data?.results?.length
    ? '-- Select Form --'
    : 'No Patient Available'

  const selectedCustomer = React.useMemo(() => {
    // @ts-ignore
    return options.find((option) => option.value === selectedCustomerId)
  }, [options, selectedCustomerId])

  return (
    <Modal
      dismissOnOutsideClick
      primaryAction
      secondaryAction
      loading={
        isLoadingChangeResponseCustomer || isLoadingChangePacketResponseCustomer
      }
      onHide={() => setIsModalOpen(false)}
      onPrimaryAction={handleChangeResponseCustomer}
      onSecondaryAction={() => setIsModalOpen(false)}
      primaryActionText="Confirm"
      show={isModalOpen}
      size="small"
      subtitle={`Are you sure you want to transfer this to ${isAnonymous ? 'a' : 'another'} patient account? Please confirm to proceed.`}
      title="Confirm Transfer"
    >
      <Space size="large">
        {/* @ts-ignore */}
        <BaseField label="Select Patient">
          {/* @ts-ignore */}
          <Select
            showOptions
            disabled={
              isLoadingLoadCustomers ||
              isLoadingChangeResponseCustomer ||
              isLoadingChangePacketResponseCustomer
            }
            isClearable={false}
            isFetching={isFetching}
            loading={isLoadingLoadCustomers}
            // @ts-ignore
            onChange={(value: CustomerOption) =>
              setSelectedCustomerId(value?.value)
            }
            onMenuScrollToBottom={onMenuScrollToBottom}
            onType={onInputChange}
            // @ts-ignore
            options={options}
            placeholder={dropdownPlaceholder}
            // @ts-ignore
            value={selectedCustomer}
          />
        </BaseField>
      </Space>
    </Modal>
  )
}

export default CustomerSelectField
